import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61558724039378";
const BOOKWHEN_URL = "https://bookwhen.com/chesshood";

const COPY = {
  badge: {
    en: "CONTACT CHESSHOOD",
    pl: "KONTAKT CHESSHOOD",
    es: "CONTACTO CHESSHOOD",
  },
  title: {
    en: "Get in touch",
    pl: "Skontaktuj sie",
    es: "Ponte en contacto",
  },
  lead: {
    en: "Chesshood is focused on chess training, AI training and chess learning. If you want to ask about lessons, training routes, school chess, junior clubs, camps, tournaments, or subscriptions, please use the contact details below.",
    pl: "Chesshood koncentruje sie na treningu szachowym, treningu AI i nauce gry. Jesli chcesz zapytac o lekcje, sciezki treningowe, szachy szkolne, kluby juniorskie, obozy, turnieje lub subskrypcje, skorzystaj z ponizszych danych kontaktowych.",
    es: "Chesshood se centra en el entrenamiento de ajedrez, el entrenamiento con IA y el aprendizaje del juego. Si quieres preguntar por clases, rutas de entrenamiento, ajedrez escolar, clubes junior, campamentos, torneos o suscripciones, usa los datos de contacto de abajo.",
  },
  metaTitle: {
    en: "Chesshood | Contact",
    pl: "Chesshood | Kontakt",
    es: "Chesshood | Contacto",
  },
  metaDescription: {
    en: "Contact Chesshood about chess training, AI training, chess learning, lessons, junior clubs, school chess, tournaments, subscriptions and local chess activity in Winchester and Colden Common by email, Facebook or web checkout.",
    pl: "Skontaktuj sie z Chesshood w sprawie treningu szachowego, treningu AI, nauki gry, lekcji, klubow juniorskich, szachow szkolnych, turniejow, subskrypcji i lokalnych zajec w Winchester oraz Colden Common przez email, Facebook lub checkout na stronie.",
    es: "Contacta con Chesshood sobre entrenamiento de ajedrez, entrenamiento con IA, aprendizaje, clases, clubes junior, ajedrez escolar, torneos, suscripciones y actividad local en Winchester y Colden Common por correo, Facebook o checkout web.",
  },
  aiTrainingEmail: {
    en: "AI training email",
    pl: "Email AI training",
    es: "Correo AI training",
  },
  aiTrainingCopy: {
    en: "Use this email for AI chess training and Chesshood platform questions.",
    pl: "Uzyj tego emaila w sprawach AI chess training i pytan o platforme Chesshood.",
    es: "Usa este correo para AI chess training y preguntas sobre la plataforma Chesshood.",
  },
  hampshireTrainingEmail: {
    en: "Hampshire training email",
    pl: "Email trening Hampshire",
    es: "Correo entrenamiento Hampshire",
  },
  hampshireTrainingCopy: {
    en: "Use this email for chess training in Hampshire, local clubs and school chess.",
    pl: "Uzyj tego emaila w sprawach treningu szachowego w Hampshire, lokalnych klubow i szachow szkolnych.",
    es: "Usa este correo para entrenamiento de ajedrez en Hampshire, clubes locales y ajedrez escolar.",
  },
  facebook: { en: "Facebook", pl: "Facebook", es: "Facebook" },
  bookwhen: { en: "Bookwhen", pl: "Bookwhen", es: "Bookwhen" },
  bookwhenCopy: {
    en: "Open the Bookwhen schedule to choose one or several options from the current list of chess training, clubs, camps and events.",
    pl: "Otworz harmonogram Bookwhen, aby wybrac jedna lub kilka opcji z aktualnej listy treningow szachowych, klubow, obozow i wydarzen.",
    es: "Abre el calendario de Bookwhen para elegir una o varias opciones de la lista actual de entrenamientos, clubes, campamentos y eventos.",
  },
  bookwhenButton: {
    en: "Book now",
    pl: "Book now",
    es: "Book now",
  },
  venue: { en: "Venue", pl: "Miejsce", es: "Lugar" },
  subscribe: { en: "Subscribe", pl: "Subskrypcja", es: "Suscripcion" },
  subscribeCopy: {
    en: "To subscribe on the website and get Chesshood Pro, use the web checkout here.",
    pl: "Aby zasubskrybowac na stronie i otrzymac Chesshood Pro, uzyj checkoutu tutaj.",
    es: "Para suscribirte en la web y obtener Chesshood Pro, usa el checkout aqui.",
  },
  subscribeButton: {
    en: "Subscribe to Chesshood Pro",
    pl: "Subskrybuj Chesshood Pro",
    es: "Suscribirse a Chesshood Pro",
  },
  contactAbout: {
    en: "You can contact me about chess training, AI training and learning",
    pl: "Mozesz skontaktowac sie ze mna w sprawie treningu szachowego, treningu AI i nauki",
    es: "Puedes contactarme sobre entrenamiento de ajedrez, entrenamiento con IA y aprendizaje",
  },
  bullets: {
    en: [
      "AI chess training and guided improvement",
      "private chess lessons and guided improvement",
      "training routes for beginners and improving players",
      "regular chess camps and junior tournaments",
      "school chess clubs and after-school chess sessions",
      "local junior club places in Winchester and Colden Common",
      "web subscriptions and Chesshood Pro access",
    ],
    pl: [
      "treningu szachowego AI i prowadzonego rozwoju",
      "prywatnych lekcji szachowych i prowadzonego rozwoju",
      "sciezek treningowych dla poczatkujacych i rozwijajacych sie graczy",
      "regularnych obozow szachowych i turniejow juniorskich",
      "szkolnych klubow szachowych i zajec pozalekcyjnych",
      "miejsc w lokalnych klubach juniorskich w Winchester i Colden Common",
      "subskrypcji webowych i dostepu do Chesshood Pro",
    ],
    es: [
      "entrenamiento de ajedrez con IA y mejora guiada",
      "clases privadas de ajedrez y mejora guiada",
      "rutas de entrenamiento para principiantes y jugadores en progreso",
      "campamentos de ajedrez regulares y torneos junior",
      "clubes escolares y sesiones extraescolares de ajedrez",
      "plazas en clubes junior de Winchester y Colden Common",
      "suscripciones web y acceso a Chesshood Pro",
    ],
  },
};

function ContactPage() {
  const { pick } = useLanguage();

  useEffect(() => {
    document.title = pick(COPY.metaTitle);

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute("content", pick(COPY.metaDescription));
  }, [pick]);

  return (
    <section className="ch-contact-page">
      <style>{`
        .ch-contact-page{
          width:100%;
          color:#f5ead0;
        }

        .ch-contact-shell{
          width:min(100%, 1040px);
          margin:0 auto;
        }

        .ch-contact-card{
          border:1.5px solid rgba(255, 210, 104, 0.82);
          border-radius:24px;
          padding:clamp(16px, 3vw, 34px);
          background:
            radial-gradient(circle at top right, rgba(58, 95, 171, 0.16), transparent 30%),
            linear-gradient(180deg, rgba(10, 18, 41, 0.96), rgba(6, 12, 28, 0.98));
          box-shadow:
            0 0 0 1px rgba(255, 235, 173, 0.08),
            0 24px 52px rgba(3, 8, 20, 0.28);
        }

        .ch-contact-badge{
          display:inline-flex;
          align-items:center;
          min-height:28px;
          padding:0 12px;
          border-radius:999px;
          border:1.5px solid rgba(255, 210, 104, 0.82);
          color:#ffe7a2;
          font-size:0.72rem;
          font-weight:900;
          letter-spacing:0.06em;
        }

        .ch-contact-title{
          margin:14px 0 10px;
          color:#fff2cb;
          font-size:clamp(2rem, 4vw, 3rem);
          font-weight:900;
          line-height:1.02;
        }

        .ch-contact-lead{
          max-width:820px;
          margin:0 0 18px;
          color:#f0dfb0;
          line-height:1.55;
          font-size:0.98rem;
          font-weight:700;
        }

        .ch-contact-grid{
          display:grid;
          grid-template-columns:1fr;
          gap:10px;
        }

        .ch-contact-panel{
          min-height:100%;
          padding:14px 14px 16px;
          border-radius:12px;
          border:1.5px solid rgba(255, 210, 104, 0.7);
          background:
            linear-gradient(180deg, rgba(32, 36, 47, 0.94), rgba(21, 26, 38, 0.98));
        }

        .ch-contact-panel-title{
          margin:0 0 10px;
          color:#ffd76d;
          font-size:0.72rem;
          font-weight:900;
          letter-spacing:0.04em;
          text-transform:uppercase;
        }

        .ch-contact-copy{
          color:#f1e1b7;
          line-height:1.55;
          font-size:0.97rem;
        }

        .ch-contact-copy p{
          margin:0;
        }

        .ch-contact-copy p + p{
          margin-top:12px;
        }

        .ch-contact-actions{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          margin-top:10px;
        }

        .ch-contact-button{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-height:34px;
          padding:0 14px;
          border-radius:10px;
          background:linear-gradient(180deg, #f0b446, #d88b20);
          color:#1b1203;
          text-decoration:none;
          font-weight:900;
          font-size:0.92rem;
          box-shadow:0 10px 20px rgba(0, 0, 0, 0.18);
        }

        .ch-contact-link{
          color:#fff1c9;
          text-decoration:none;
          font-weight:800;
          word-break:break-word;
        }

        .ch-contact-link:hover,
        .ch-contact-link:focus-visible,
        .ch-contact-button:hover,
        .ch-contact-button:focus-visible{
          filter:brightness(1.05);
        }

        .ch-contact-footer{
          margin-top:8px;
        }

        .ch-contact-footer-title{
          margin:0 0 8px;
          color:#fff2cb;
          font-size:clamp(1.5rem, 2.4vw, 2.05rem);
          font-weight:900;
          line-height:1.05;
        }

        .ch-contact-footer-list{
          margin:0;
          padding-left:18px;
          color:#f1e1b7;
          line-height:1.7;
          font-size:0.95rem;
        }

        @media (max-width: 900px){
          .ch-contact-footer{
            margin-top:18px;
          }
        }

        @media (max-width: 640px){
          .ch-contact-card{
            border-radius:18px;
            padding:14px;
          }

          .ch-contact-title{
            font-size:2rem;
          }

          .ch-contact-panel{
            border-radius:14px;
          }
        }
      `}</style>

      <div className="ch-contact-shell">
        <div className="ch-contact-card">
          <div className="ch-contact-badge">{pick(COPY.badge)}</div>
          <h1 className="ch-contact-title">{pick(COPY.title)}</h1>
          <p className="ch-contact-lead">{pick(COPY.lead)}</p>

          <div className="ch-contact-grid">
            <article className="ch-contact-panel">
              <h2 className="ch-contact-panel-title">{pick(COPY.aiTrainingEmail)}</h2>
              <div className="ch-contact-copy">
                <p>{pick(COPY.aiTrainingCopy)}</p>
              </div>
              <div className="ch-contact-actions">
                <a href="mailto:chesshood@chesshood.com" className="ch-contact-link">
                  chesshood@chesshood.com
                </a>
              </div>
            </article>

            <article className="ch-contact-panel">
              <h2 className="ch-contact-panel-title">
                {pick(COPY.hampshireTrainingEmail)}
              </h2>
              <div className="ch-contact-copy">
                <p>{pick(COPY.hampshireTrainingCopy)}</p>
              </div>
              <div className="ch-contact-actions">
                <a href="mailto:chesshampshire@gmail.com" className="ch-contact-link">
                  chesshampshire@gmail.com
                </a>
              </div>
            </article>

            <article className="ch-contact-panel">
              <h2 className="ch-contact-panel-title">{pick(COPY.facebook)}</h2>
              <div className="ch-contact-actions">
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ch-contact-button"
                >
                  Facebook
                </a>
              </div>
            </article>

            <article className="ch-contact-panel">
              <h2 className="ch-contact-panel-title">{pick(COPY.bookwhen)}</h2>
              <div className="ch-contact-copy">
                <p>{pick(COPY.bookwhenCopy)}</p>
              </div>
              <div className="ch-contact-actions">
                <a
                  href={BOOKWHEN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ch-contact-button"
                >
                  {pick(COPY.bookwhenButton)}
                </a>
              </div>
            </article>

            <article className="ch-contact-panel">
              <h2 className="ch-contact-panel-title">{pick(COPY.venue)}</h2>
              <div className="ch-contact-copy">
                <p>Badger Farm Community Centre</p>
                <p>2 Badger Farm Road, Winchester SO22 4QB</p>
              </div>
            </article>

            <article className="ch-contact-panel">
              <h2 className="ch-contact-panel-title">{pick(COPY.subscribe)}</h2>
              <div className="ch-contact-copy">
                <p>{pick(COPY.subscribeCopy)}</p>
              </div>
              <div className="ch-contact-actions">
                <Link to="/checkout" className="ch-contact-button">
                  {pick(COPY.subscribeButton)}
                </Link>
              </div>
            </article>
          </div>

          <div className="ch-contact-footer">
            <h2 className="ch-contact-footer-title">{pick(COPY.contactAbout)}</h2>
            <ul className="ch-contact-footer-list">
              {pick(COPY.bullets).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactPage;
